<template>
    <el-radio
        v-model="localModel"
        :label="elRadioValue"
        :name="name"
        :disabled="disabled"
        :border="border"
        :data-test="dataTestName"
        :data-testid="dataTestName || 'input-radio'"
        :size="size"
        :aria-hidden="false"
        class="redesign-base-radio"
        v-bind="$attrs"
        v-on="listeners"
    >
        <slot>
            {{ label }}
        </slot>
    </el-radio>
</template>

<script setup lang="ts">
import { computed, useListeners } from 'vue'
import { Radio as ElRadio } from 'element-ui'

type ModelType = string | number | boolean | undefined

const listeners = useListeners()

interface IProps {
    modelValue?: ModelType
    value?: ModelType
    dataValue?: ModelType
    label?: string
    size?: 'medium' | 'small' | 'mini'
    name?: string
    dataTestName?: string
    disabled?: boolean
    border?: boolean
}

const props = withDefaults(
    defineProps<IProps>(),
    {
        modelValue: undefined,
        value: undefined,
        dataValue: undefined,
        label: '',
        name: '',
        dataTestName: 'base-radio',
        disabled: false,
        border: false,
        size: 'small'
    }
)

const emit = defineEmits<{
    (e: 'input', pl: ModelType): void
    (e: 'change', pl: ModelType): void
    (e: 'update:modelValue', pl: ModelType): void
}>()

const localModel = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('change', val)
        emit('update:modelValue', val)
    }
})
const elRadioValue = computed(() => {
    return props.dataValue ?? props.label
})
</script>

<style lang="scss">
    .el-radio.redesign-base-radio {
        @apply relative inline-flex items-center;
        color: var(--mainTextColor-color);
        .el-radio__inner:hover {
            border-color: var(--primary-color);
        }
        .el-radio__input {
            @apply inline-flex items-center justify-center;
        }
        .el-radio__input.is-checked {

            + .el-radio__label {
                color: var(--primary-color);
            }

            .el-radio__inner {
                border-color: var(--primary-color);
                background: var(--empty-bg);
            }
        }
        .el-radio__inner {
            height: 18px;
            width: 18px;
            min-width: 18px;
            border-width: 2px;

            &::after {
                height: 10px;
                width: 10px;
            }

        }
        &:focus:not(.is-focus):not(:active):not(.is-disabled) {
            .el-radio__inner {
                box-shadow: 0 0 2px 2px var(--active-elements) !important;
            }
        }

        &.is-checked {
            .el-radio__inner {
                &::after {
                    @apply bg-active-elements;
                }
            }
        }

        &.is-bordered {
            @apply py-1 px-3;
            .el-radio__inner {
                width: 16px;
                height: 16px;
                min-width: 16px;
            }
            &.is-checked {
                @apply border-active-elements;
            }
        }
    }

    [dir='rtl'] {
        .el-radio.redesign-base-radio {
            margin-right: 0;
            &:not(:last-of-type) {
                margin-left: 12px;
            }
            .el-radio__inner {
                @apply ml-2;
            }
        }
    }
</style>
